/**
 * The SKU a product listing shows to B2B members (GROW-251): the product card
 * and the quick order row print it so a retailer can see exactly which article
 * they are ordering. Consumers never see it.
 *
 * One rule for every surface, so a SKU can never be shown on one listing and
 * withheld on another by accident.
 */

import type { ProductVariant } from "@/types/product";
import { useIsB2b } from "@/hooks/useIsB2b";

/**
 * What a listing entry has to carry for its SKU to be shown. Both an `ItemCard`
 * and a `ProductCardBlock` satisfy it.
 */
export interface ProductSkuSource {
  sku?: string | null;
  variants?: readonly ProductVariant[] | null;
}

/**
 * The SKU to print for a product, or null when there is none to print.
 *
 * A product sold in versions prints none. A listing entry chooses no version,
 * and the product's own SKU is not a neutral family code: an accessory node
 * carries the SKU of one of its versions (typically the first, e.g. the XS
 * collar's), so printing it would name an article the retailer may not be
 * buying. A CMS-authored card does not receive its product's versions, so the
 * backend applies the same rule there and sends no SKU. Bundles have no SKU of
 * their own and print nothing either.
 */
export function getDisplaySku(product: ProductSkuSource): string | null {
  if (product.variants?.length) return null;
  return product.sku?.trim() || null;
}

/**
 * {@link getDisplaySku} for B2B members; null for consumers, and until the cart
 * has loaded (see `useIsB2b`).
 */
export function useProductSku(product: ProductSkuSource): string | null {
  const isB2b = useIsB2b();
  return isB2b ? getDisplaySku(product) : null;
}
